/**
 * GlobalAuthGate — mount-once component at the App root.
 *
 * Listens for `window` "torahya:auth-required" events and shows the
 * sign-up modal (Hebrew or English, driven by react-i18next). Any code
 * anywhere in the app can trigger the gate without importing the hook
 * by calling:
 *
 *   import { requireAuth } from "../lib/authGate";
 *   if (!requireAuth("basket")) return;   // returns true if authed,
 *                                          // shows modal + returns false otherwise.
 *
 * This avoids threading `useRequireAuth` through every leaf component
 * (basket-add, bookmark-toggle, save-to-notebook, etc.) while still
 * giving each call-site a single line to add. The modal itself is
 * defined here, in one place; copy lives in i18n under `require_auth.*`.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { GraduationCap } from "lucide-react";

// Maps each `feature` key (emitted by callers) to a pair of i18n keys
// under the `require_auth.*` namespace. Hebrew + English copy stay
// fully parallel in en.json / he.json — see <require_auth> block.
const FEATURE_KEYS = {
  default: { title: "default_title", body: "default_body" },
  lesson: { title: "lesson_title", body: "lesson_body" },
  basket: { title: "basket_title", body: "basket_body" },
  ai: { title: "ai_title", body: "ai_body" },
  who_says_what: { title: "who_says_what_title", body: "who_says_what_body" },
  notebook: { title: "notebook_title", body: "notebook_body" },
  beit_midrash: { title: "beit_midrash_title", body: "beit_midrash_body" },
  bookmark: { title: "bookmark_title", body: "bookmark_body" },
  highlight: { title: "highlight_title", body: "highlight_body" },
  note: { title: "note_title", body: "note_body" },
};

export default function GlobalAuthGate() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("default");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    function handler(ev) {
      // Authed users never see the modal — but we still listen so we can
      // ignore stale events fired during a brief logout/refresh race.
      if (user) return;
      const f = (ev?.detail && ev.detail.feature) || "default";
      setFeature(f);
      setOpen(true);
    }
    window.addEventListener("torahya:auth-required", handler);
    return () => window.removeEventListener("torahya:auth-required", handler);
  }, [user]);

  const goToAuth = () => {
    setOpen(false);
    const next = location.pathname + location.search;
    navigate(`/auth?next=${encodeURIComponent(next)}`);
  };

  const keys = FEATURE_KEYS[feature] || FEATURE_KEYS.default;
  const title = t(`require_auth.${keys.title}`);
  const body = t(`require_auth.${keys.body}`);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="require-auth-dialog"
        className="max-w-md border-gold/40"
      >
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-2">
            <GraduationCap className="h-7 w-7 text-gold" strokeWidth={1.5} />
          </div>
          <DialogTitle className="font-heading text-center text-2xl text-foreground">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center leading-relaxed pt-2 text-foreground/80">
            {body}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 pt-2">
          <Button
            data-testid="require-auth-signup-btn"
            onClick={goToAuth}
            className="bg-primary hover:bg-primary/90 text-primary-foreground font-heading min-h-[44px]"
          >
            {t("require_auth.signup_now")}
          </Button>
          <Button
            data-testid="require-auth-dismiss-btn"
            variant="ghost"
            onClick={() => setOpen(false)}
            className="text-muted-foreground hover:text-foreground min-h-[40px]"
          >
            {t("require_auth.maybe_later")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
